import { Box, IconButton } from '@mui/material';
import { useRef, useState, useEffect } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AttachmentThumbnail from './AttachmentThumbnail';

// Thumbnail width (48px) + gap (8px)
const _thumbSlotWidth = 56;

/**
 * AttachmentStripThumbs — scrollable thumbnail row with prev/next nav.
 * Nav buttons only appear when thumbnails don't fit available width.
 * @param {object} props
 * @param {object[]} props.documents - array of document entities
 * @param {Function} props.onLightBox - fn(documents, index) called on image click
 */
export default function AttachmentStripThumbs({ documents, onLightBox }) {
    const rootRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(4);
    const [showNav, setShowNav] = useState(false);
    const [offset, setOffset] = useState(0);

    // Measure container width — recalculate on resize or document count change
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;

        const observer = new ResizeObserver(() => {
            const count = Math.max(1, Math.floor(el.clientWidth / _thumbSlotWidth));
            setVisibleCount(count);
            setShowNav(documents.length > count);
        });

        observer.observe(el);
        return () => observer.disconnect();
    }, [documents.length]);

    // Reset offset when documents change (entry switch)
    useEffect(() => {
        setOffset(0);
    }, [documents]);

    const handlePrev = () => {
        setOffset((o) => Math.max(0, o - 1));
    };

    const handleNext = () => {
        setOffset((o) => Math.min(documents.length - visibleCount, o + 1));
    };

    const visible = documents.slice(offset, offset + visibleCount);

    return (
        <Box ref={rootRef} id="attachment-strip-thumbs-row" className="attachment-strip-thumbs-row">
            {showNav && (
                <IconButton onClick={handlePrev} disabled={offset === 0} size="small" className="attachment-strip-nav-btn">
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
            )}

            <Box id="attachment-strip-thumbs" className="attachment-strip-thumbs">
                {visible.map((doc, i) => (
                    <AttachmentThumbnail
                        key={doc.rk || `thumb-${offset + i}`}
                        document={doc}
                        // Absolute index in full documents array — needed for lightbox prev/next
                        index={offset + i}
                        // Pass full documents array so ContentPanel.handleLightBox receives (documents, index)
                        onLightBox={(index) => onLightBox(documents, index)}
                    />
                ))}
            </Box>

            {showNav && (
                <IconButton onClick={handleNext} disabled={offset + visibleCount >= documents.length} size="small" className="attachment-strip-nav-btn">
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
}
